/**
 * Fill presets for the Shape Fill menu: gradient variations, textures and helpers.
 * Textures are self-contained SVG data URLs (feTurbulence noise over a base colour), stored as tiled image fills
 * so they work for CSS-rendered rectangles, SVG paths (via <pattern>) and exports alike.
 */
import { tint, shade } from './colors';
import { normalizeGradient, gradientToCss, colorAt } from './gradients';

/* ------------------------------------------------------------------ */
/* Gradients                                                           */
/* ------------------------------------------------------------------ */

const DIRS = [
  ['Linear Diagonal - Top Left to Bottom Right', 135], ['Linear Down', 180], ['Linear Diagonal - Top Right to Bottom Left', 225],
  ['Linear Right', 90], ['From Center', 'radial'], ['Linear Left', 270],
  ['Linear Diagonal - Bottom Left to Top Right', 45], ['Linear Up', 0], ['Linear Diagonal - Bottom Right to Top Left', 315],
];

const gradientFill = (c1, c2, dir) => ({
  type: 'gradient',
  gradient: dir === 'radial' ? { type: 'radial', angle: 0, stops: [{ color: c1, position: 0 }, { color: c2, position: 100 }] } : { type: 'linear', angle: dir, stops: [{ color: c1, position: 0 }, { color: c2, position: 100 }] },
});

/** 9 light + 9 dark variations of a base colour, like Publisher's Gradient submenu. */
export const gradientVariations = (base = '#4a6d8c') => ({
  light: DIRS.map(([name, dir]) => ({ id: `light-${dir}`, name: `${name} (light)`, fill: gradientFill(tint(base, 0.75), base, dir) })),
  dark: DIRS.map(([name, dir]) => ({ id: `dark-${dir}`, name: `${name} (dark)`, fill: gradientFill(base, shade(base, 0.55), dir) })),
});

/* ------------------------------------------------------------------ */
/* Textures                                                            */
/* ------------------------------------------------------------------ */

// Quotes are encoded too so the URL is safe inside an unquoted CSS url(...) and in JSON.
const svgUrl = (svg) => `data:image/svg+xml;utf8,${encodeURIComponent(svg.replace(/\s+/g, ' ').trim()).replace(/'/g, '%27').replace(/\(/g, '%28').replace(/\)/g, '%29')}`;

const noise = ({ base, ink = '#000000', freq = '0.8', octaves = 3, opacity = 0.22, seed = 3, size = 160, extra = '' }) => svgUrl(`
  <svg xmlns='http://www.w3.org/2000/svg' width='${size}' height='${size}'>
    <rect width='100%' height='100%' fill='${base}'/>
    <filter id='n' x='0' y='0' width='100%' height='100%'>
      <feTurbulence type='fractalNoise' baseFrequency='${freq}' numOctaves='${octaves}' seed='${seed}' stitchTiles='stitch' result='t'/>
      <feColorMatrix in='t' type='saturate' values='0' result='g'/>
      <feComponentTransfer in='g'><feFuncA type='linear' slope='${opacity * 3}' intercept='-${opacity}'/></feComponentTransfer>
      <feComposite operator='in' in2='SourceGraphic'/>
    </filter>
    <rect width='100%' height='100%' fill='${ink}' filter='url(#n)' opacity='${Math.min(1, opacity * 2.2)}'/>
    ${extra}
  </svg>`);

const lines = (color, gap, width, angle, opacity) => `<rect width='100%' height='100%' fill='url(#l)' opacity='${opacity}'/><defs><pattern id='l' width='${gap}' height='${gap}' patternUnits='userSpaceOnUse' patternTransform='rotate(${angle})'><rect width='${width}' height='${gap}' fill='${color}'/></pattern></defs>`;

export const TEXTURES = [
  { id: 'newsprint', name: 'Newsprint', url: noise({ base: '#f2efe8', ink: '#5a5650', freq: '0.9', octaves: 2, opacity: 0.16, seed: 7 }) },
  { id: 'recycled', name: 'Recycled Paper', url: noise({ base: '#e8e1d2', ink: '#6b5f4a', freq: '0.35', octaves: 3, opacity: 0.2, seed: 11 }) },
  { id: 'parchment', name: 'Parchment', url: noise({ base: '#efdcb2', ink: '#8a5a1e', freq: '0.06', octaves: 4, opacity: 0.28, seed: 2 }) },
  { id: 'stationery', name: 'Stationery', url: noise({ base: '#f7f5ef', ink: '#9a8f7a', freq: '1.2', octaves: 1, opacity: 0.09, seed: 5 }) },
  { id: 'canvas', name: 'Canvas', url: noise({ base: '#ded7c3', ink: '#5b533f', freq: '0.6', octaves: 2, opacity: 0.18, seed: 9, extra: lines('#5b533f', 4, 1, 0, 0.12) + lines('#5b533f', 4, 1, 90, 0.12) }) },
  { id: 'denim', name: 'Denim', url: noise({ base: '#3b5a86', ink: '#0b1a33', freq: '0.7', octaves: 2, opacity: 0.25, seed: 4, extra: lines('#1d3559', 3, 1, 45, 0.5) }) },
  { id: 'woven', name: 'Woven Mat', url: noise({ base: '#c9a86a', ink: '#5a3d12', freq: '0.5', octaves: 2, opacity: 0.2, seed: 6, extra: lines('#6d4a15', 8, 3, 0, 0.35) + lines('#6d4a15', 8, 3, 90, 0.35) }) },
  { id: 'cork', name: 'Cork', url: noise({ base: '#c78e55', ink: '#4a2a0d', freq: '0.18', octaves: 4, opacity: 0.35, seed: 8 }) },
  { id: 'wood', name: 'Oak', url: noise({ base: '#b8843f', ink: '#4a2c0a', freq: '0.02 0.6', octaves: 3, opacity: 0.32, seed: 12 }) },
  { id: 'walnut', name: 'Walnut', url: noise({ base: '#5b3a1f', ink: '#1a0d04', freq: '0.015 0.5', octaves: 3, opacity: 0.38, seed: 13 }) },
  { id: 'marble-white', name: 'White Marble', url: noise({ base: '#f4f4f2', ink: '#5e6472', freq: '0.012', octaves: 5, opacity: 0.32, seed: 21 }) },
  { id: 'marble-green', name: 'Green Marble', url: noise({ base: '#1f4d3a', ink: '#dfeee5', freq: '0.015', octaves: 5, opacity: 0.35, seed: 22 }) },
  { id: 'granite', name: 'Granite', url: noise({ base: '#8d8f93', ink: '#1c1d21', freq: '1.4', octaves: 2, opacity: 0.3, seed: 14 }) },
  { id: 'sand', name: 'Sand', url: noise({ base: '#e6d3a3', ink: '#8a6a2a', freq: '1.6', octaves: 1, opacity: 0.16, seed: 15 }) },
  { id: 'water', name: 'Water Droplets', url: noise({ base: '#5aa9d6', ink: '#ffffff', freq: '0.05', octaves: 3, opacity: 0.3, seed: 16 }) },
  { id: 'brushed-metal', name: 'Brushed Metal', url: noise({ base: '#b9bcc2', ink: '#2c2f36', freq: '0.008 1.5', octaves: 2, opacity: 0.22, seed: 17 }) },
  { id: 'medium-wood', name: 'Medium Wood', url: noise({ base: '#9a6a3a', ink: '#3b2008', freq: '0.03 0.45', octaves: 3, opacity: 0.3, seed: 18 }) },
  { id: 'purple-mesh', name: 'Purple Mesh', url: noise({ base: '#6d4aa3', ink: '#25143f', freq: '0.9', octaves: 2, opacity: 0.22, seed: 19, extra: lines('#25143f', 6, 1, 45, 0.3) + lines('#25143f', 6, 1, -45, 0.3) }) },
];

export const textureFill = (t) => ({ type: 'image', image: { url: t.url, fit: 'tile' }, texture: t.id, color: '#ffffff' });

/* ------------------------------------------------------------------ */
/* Outline helpers                                                     */
/* ------------------------------------------------------------------ */

export const LINE_WEIGHTS = [0.25, 0.5, 0.75, 1, 1.5, 2.25, 3, 4.5, 6];

export const DASH_STYLES = [
  { id: 'solid', name: 'Solid', array: undefined, css: 'solid' },
  { id: 'dotted', name: 'Round Dot', array: '1 3', css: 'dotted' },
  { id: 'square-dot', name: 'Square Dot', array: '2 2', css: 'dotted' },
  { id: 'dashed', name: 'Dash', array: '6 4', css: 'dashed' },
  { id: 'dash-dot', name: 'Dash Dot', array: '8 4 2 4', css: 'dashed' },
  { id: 'long-dash', name: 'Long Dash', array: '12 6', css: 'dashed' },
  { id: 'long-dash-dot', name: 'Long Dash Dot', array: '12 4 2 4', css: 'dashed' },
  { id: 'long-dash-dot-dot', name: 'Long Dash Dot Dot', array: '12 4 2 4 2 4', css: 'dashed' },
];

/** SVG stroke-dasharray for a dash id (undefined = solid). */
export const dashArray = (dash) => DASH_STYLES.find((d) => d.id === dash)?.array;
/** Closest CSS border-style for a dash id. */
export const cssDash = (dash) => DASH_STYLES.find((d) => d.id === dash)?.css || 'solid';

/* ------------------------------------------------------------------ */
/* Gradient outlines                                                   */
/* ------------------------------------------------------------------ */
/*
 * An outline is `stroke = { width, color, dash, gradient? }`. With `gradient` (the shared gradient model, see
 * lib/gradients.js) the stroke is painted with the gradient — through the same creator as fills — and `color` stays the
 * solid fallback (older readers, arrowheads). Rendering: SVG paths reference a gradient paint server; CSS-rendered boxes
 * (rectangles, ellipses, text boxes, picture frames) keep a transparent border for layout and draw the gradient in an
 * `OutlineSvg` overlay (components/editor/OutlineSvg.js).
 */

/** Is the outline painted with a gradient? */
export const strokeHasGradient = (stroke) => !!(stroke && stroke.gradient && typeof stroke.gradient === 'object');

/** CSS for an outline swatch (gradient or colour); null when there is no visible outline. */
export const strokeSwatchCss = (stroke) => {
  if (!stroke || !(stroke.width > 0)) return null;
  return strokeHasGradient(stroke) ? gradientToCss(stroke.gradient) : stroke.color || '#000000';
};

/** A solid outline in `color` (drops any gradient and shimmer); `width` defaults to the current weight (or 1). */
export const solidStroke = (stroke, color, width) => {
  const { gradient, shimmer, ...rest } = stroke || {};
  return { ...rest, color: color || rest.color || '#000000', width: width ?? (stroke?.width || 1) };
};

/** A gradient outline: keeps weight and dash, gives a hidden outline a visible weight, keeps a solid fallback colour. */
export const gradientStroke = (stroke, gradient) => {
  const g = normalizeGradient(gradient);
  return { ...(stroke || {}), color: stroke?.color || g.stops[0].color, width: stroke?.width || 3, gradient: g };
};

/**
 * Shimmer — a soft highlight that sweeps across a gradient outline (`stroke.shimmer`). Screen only: print, export and
 * thumbnails leave it out (`.wp-shimmer` is hidden there), and it respects prefers-reduced-motion.
 */
export const strokeHasShimmer = (stroke) => strokeHasGradient(stroke) && !!stroke.shimmer;
/** A shimmering metallic outline in one click: the gradient plus the animated highlight. */
export const shimmerStroke = (stroke, gradient) => ({ ...gradientStroke(stroke, gradient), shimmer: true });
/** The metallic presets offered as one-click shimmer tiles (ids from GRADIENT_PRESETS). */
export const SHIMMER_PRESET_IDS = ['gold', 'silver', 'copper'];

/* Tables keep their borders as flat properties (borderWidth / borderColor / borderGradient / borderShimmer); these two
   adapters let the outline menus and the Outline Gradient dialog treat them like any other stroke. */
export const tableBorderStroke = (p = {}) => ({ width: p.borderWidth ?? 1, color: p.borderColor || '#000000', gradient: p.borderGradient || undefined, shimmer: !!p.borderShimmer });
export const tableBorderProps = (stroke = {}) => ({ borderWidth: stroke.width ?? 1, borderColor: stroke.color || '#000000', borderGradient: strokeHasGradient(stroke) ? stroke.gradient : null, borderShimmer: strokeHasShimmer(stroke) });

/** Arrowhead colours for an outline: a gradient's colours at the two ends of the run, otherwise the stroke colour. */
export const strokeEndColors = (stroke) => {
  const c = stroke?.color || '#000000';
  if (!strokeHasGradient(stroke)) return { start: c, end: c };
  return { start: colorAt(stroke.gradient, 0).color, end: colorAt(stroke.gradient, 100).color };
};
